import React, { useState, useEffect } from "react";
import {
  Box, Card, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Container, Typography, Chip, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Avatar, Stack, IconButton, Tooltip, Divider,
  TablePagination, Tabs, Tab,
} from "@mui/material";
import { Icon } from "@iconify/react";
import {
  collection, onSnapshot, query, orderBy, doc, updateDoc,
  deleteDoc, addDoc, serverTimestamp, where,
} from "firebase/firestore";
import { db } from "../database/db";
import Page from "../components/Page";
import Scrollbar from "../components/Scrollbar";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Map real Firebase "report" fields to display-friendly shape
const normalizeReport = (r) => ({
  ...r,
  contentPreview: r.description || r.content || r.contentPreview || "—",
  timestamp: r.report_time || r.timestamp || null,
});

const VIOLATION_COLORS = {
  spam: "warning",
  "hate speech": "error",
  "market manipulation": "error",
  misinformation: "warning",
  harassment: "error",
  "explicit content": "error",
  other: "default",
};

export default function ContentModeration() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0); // 0=pending, 1=actioned, 2=dismissed
  const [selected, setSelected] = useState(null);
  const [noteDialog, setNoteDialog] = useState({ open: false, report: null, note: "" });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);

  useEffect(() => {
    // Firebase collection: "report" — fields: description, report_time (Unix ms), status (added by admin)
    const q = query(collection(db, "report"), orderBy("report_time", "desc"));
    const unsub = onSnapshot(q, (snap) => {
      setReports(snap.docs.map((d) => normalizeReport({ id: d.id, ...d.data() })));
      setLoading(false);
    }, () => setLoading(false));
    return unsub;
  }, []);

  const statuses = ["pending", "actioned", "dismissed"];
  const filtered = reports.filter((r) => (r.status || "pending") === statuses[tab]);
  const paginated = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleRemoveContent = async (report) => {
    try {
      // Delete the actual post/comment
      if (report.contentType === "post" && report.postId) {
        await deleteDoc(doc(db, "Posts", report.postId));
      } else if (report.contentType === "comment" && report.commentId) {
        await deleteDoc(doc(db, "Comments", report.commentId));
      }
      // Mark report actioned
      await updateDoc(doc(db, "report", report.id), {
        status: "actioned",
        actionedAt: serverTimestamp(),
        action: "content-removed",
      });
      // Log to AuditLog
      await addDoc(collection(db, "AuditLog"), {
        action: "content-remove",
        targetName: report.authorUsername || report.postId,
        details: { type: report.contentType, violation: report.violationType },
        timestamp: serverTimestamp(),
      });
      toast.success("Content removed");
    } catch (err) {
      toast.error("Failed: " + err.message);
    }
  };

  const handleWarnUser = async (report) => {
    try {
      // Add a violation to the user
      if (report.authorId) {
        const userRef = doc(db, "User", report.authorId);
        const { getDoc } = await import("firebase/firestore");
        const snap = await getDoc(userRef);
        if (snap.exists()) {
          const current = snap.data().violations || 0;
          await updateDoc(userRef, { violations: current + 1 });
        }
      }
      await updateDoc(doc(db, "report", report.id), {
        status: "actioned",
        actionedAt: serverTimestamp(),
        action: "user-warned",
      });
      await addDoc(collection(db, "AuditLog"), {
        action: "content-remove",
        targetName: report.authorUsername,
        details: { action: "user-warned", violation: report.violationType },
        timestamp: serverTimestamp(),
      });
      toast.success(`Warning added to ${report.authorUsername}`);
    } catch (err) {
      toast.error("Failed: " + err.message);
    }
  };

  const handleDismiss = async (report) => {
    try {
      await updateDoc(doc(db, "report", report.id), {
        status: "dismissed",
        actionedAt: serverTimestamp(),
      });
      await addDoc(collection(db, "AuditLog"), {
        action: "report-dismiss",
        targetName: report.authorUsername || "unknown",
        details: { type: report.contentType },
        timestamp: serverTimestamp(),
      });
      toast.success("Report dismissed");
    } catch (err) {
      toast.error("Failed");
    }
  };

  const handleSaveNote = async () => {
    try {
      await updateDoc(doc(db, "report", noteDialog.report.id), {
        adminNote: noteDialog.note,
        noteUpdatedAt: serverTimestamp(),
      });
      toast.success("Note saved");
      setNoteDialog({ open: false, report: null, note: "" });
    } catch (err) {
      toast.error("Failed");
    }
  };

  const formatTime = (ts) => {
    if (!ts) return "—";
    // report_time is Unix ms; Firestore timestamps have .toDate()
    if (ts.toDate) return ts.toDate().toLocaleString();
    return new Date(typeof ts === "number" ? ts : Number(ts)).toLocaleString();
  };


  const counts = statuses.map((s) => reports.filter((r) => (r.status || "pending") === s).length);

  return (
    <Page title="Content Moderation | Fire Investing Admin">
      <ToastContainer position="top-right" />
      <Container maxWidth="xl">
        <Box sx={{ pb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>Content Moderation</Typography>
          <Typography variant="body2" color="text.secondary">
            Reported posts and comments — review and take action
          </Typography>
        </Box>

        <Tabs value={tab} onChange={(_, v) => { setTab(v); setPage(0); }} sx={{ mb: 3 }}>
          <Tab label={`Pending (${counts[0]})`} />
          <Tab label={`Actioned (${counts[1]})`} />
          <Tab label={`Dismissed (${counts[2]})`} />
        </Tabs>

        <Card sx={{ borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.25)" }}>
          <Scrollbar>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Content</TableCell>
                    <TableCell>Author</TableCell>
                    <TableCell>Violation</TableCell>
                    <TableCell>Reported By</TableCell>
                    <TableCell>Time</TableCell>
                    {tab === 0 && <TableCell align="right">Actions</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginated.map((report) => (
                    <TableRow key={report.id} hover
                      onClick={() => setSelected(selected?.id === report.id ? null : report)}
                      sx={{ cursor: "pointer", bgcolor: selected?.id === report.id ? "rgba(61,193,14,0.08)" : "inherit" }}>
                      <TableCell sx={{ maxWidth: 280 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", textTransform: "uppercase", fontSize: 10 }}>
                          {report.contentType || "post"}
                        </Typography>
                        <Typography variant="body2" sx={{
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                          maxWidth: 260
                        }}>
                          {report.contentPreview || report.content || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Avatar src={report.authorPhoto} sx={{ width: 28, height: 28, fontSize: 12 }}>
                            {(report.authorUsername || "?").charAt(0).toUpperCase()}
                          </Avatar>
                          <Typography variant="body2">@{report.authorUsername || "unknown"}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={report.violationType || "other"}
                          size="small"
                          color={VIOLATION_COLORS[report.violationType] || "default"}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          @{report.reportedBy || "anonymous"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{formatTime(report.timestamp)}</Typography>
                      </TableCell>
                      {tab === 0 && (
                        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            <Tooltip title="Remove content">
                              <IconButton size="small" color="error" onClick={() => handleRemoveContent(report)}>
                                <Icon icon="fe:trash" width={16} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Warn user (+1 violation)">
                              <IconButton size="small" color="warning" onClick={() => handleWarnUser(report)}>
                                <Icon icon="eva:alert-triangle-fill" width={16} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Add note">
                              <IconButton size="small" onClick={() => setNoteDialog({ open: true, report, note: report.adminNote || "" })}>
                                <Icon icon="eva:edit-fill" width={16} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Dismiss">
                              <IconButton size="small" onClick={() => handleDismiss(report)}>
                                <Icon icon="eva:close-circle-fill" width={16} />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                  {/* Expanded detail row */}
                  {selected && paginated.some((r) => r.id === selected.id) && (
                    <TableRow>
                      <TableCell colSpan={tab === 0 ? 6 : 5} sx={{ bgcolor: "background.default", p: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>Full Content</Typography>
                        <Typography variant="body2" sx={{ p: 2, bgcolor: "background.paper", borderRadius: 1, fontFamily: "monospace", whiteSpace: "pre-wrap" }}>
                          {selected.content || selected.contentPreview || "Content not available"}
                        </Typography>
                        {selected.adminNote && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                            Admin note: {selected.adminNote}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                  {paginated.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                          {loading ? "Loading reports..." : "No reports in this category"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>
          <TablePagination rowsPerPageOptions={[15, 25, 50]} component="div"
            count={filtered.length} rowsPerPage={rowsPerPage} page={page}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }} />
        </Card>
      </Container>

      {/* Note Dialog */}
      <Dialog open={noteDialog.open} onClose={() => setNoteDialog({ open: false, report: null, note: "" })} maxWidth="xs" fullWidth>
        <DialogTitle>Admin Note</DialogTitle>
        <DialogContent>
          <TextField fullWidth multiline rows={3} label="Note" value={noteDialog.note}
            onChange={(e) => setNoteDialog((p) => ({ ...p, note: e.target.value }))} sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoteDialog({ open: false, report: null, note: "" })}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveNote}>Save</Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
}
